// src/pages/HomePage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api, { LANG_KEY } from "../lib/api";

import FeaturedGrid from "../components/FeaturedGrid";
import TrendingSection from "../components/TrendingSection";
import NewsGridThree from "../components/NewsGridThree";
import NewsGridThreePlay from "../components/NewsGridThreePlay";
import NewsWithSidebar from "../components/NewsWithSidebar";
import SearchWidget from "./SearchPage";

import HeroSkeleton from "../components/HeroSkeleton";
import { useTranslation } from "../i18n";
const STORAGE_LANG_KEY = LANG_KEY || "site_lang";

// ---- helpers ----
const normalize = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  if (typeof data.results === "object" && data.results !== null) return Object.values(data.results);
  return [];
};

const dateValue = (a) => new Date(a?.published_at || a?.created_at || 0).getTime();
const sortByDateDesc = (arr) => [...arr].sort((x, y) => dateValue(y) - dateValue(x));

const getArticleCatKeys = (article) => {
  const cats = Array.isArray(article?.categories) ? article.categories : [];
  const keys = [];
  for (const c of cats) {
    if (c?.slug) keys.push(String(c.slug));
    else if (c?.id != null) keys.push(String(c.id));
  }
  return keys;
};

const uniqBySlugId = (arr) => {
  const seen = new Set();
  return arr.filter((a) => {
    const key = String(a?.slug ?? a?.id ?? "");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
function isCanceledError(err) {
  if (!err) return false;
  if (err?.code === "ERR_CANCELED") return true;
  if (err?.name === "CanceledError" || err?.name === "AbortError") return true;
  if (typeof err?.message === "string" && err.message.toLowerCase().includes("canceled")) return true;
  try {
    if (axios.isCancel && axios.isCancel(err)) return true;
  } catch (e) {}
  return false;
}

const HomePage = () => {
  const { t } = useTranslation();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserLoading, setCurrentUserLoading] = useState(false);

  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_LANG_KEY) || "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    const onSiteLang = (e) => {
      const newLang = e?.detail?.lang || localStorage.getItem(STORAGE_LANG_KEY) || "en";
      if (newLang && newLang !== lang) setLang(newLang);
    };
    const onStorage = (e) => {
      if (e.key === STORAGE_LANG_KEY) {
        const newLang = e.newValue || "en";
        if (newLang !== lang) setLang(newLang);
      }
    };
    window.addEventListener("site:language_changed", onSiteLang);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("site:language_changed", onSiteLang);
      window.removeEventListener("storage", onStorage);
    };
  }, [lang]);

  const fetchAll = useCallback(async (signal) => {
    setLoading(true);
    setErr(null);
    try {
      const [aRes, cRes, tRes] = await Promise.all([
        api.get("/articles/", {
          signal,
          params: { status: "published", page_size: 80, ordering: "-created_at", lang },
        }),
        api.get("/categories/", { signal, params: { is_active: true, page_size: 200, lang } }),
        api.get("/tags/", { signal, params: { page_size: 200, lang } }),
      ]);
      setArticles(sortByDateDesc(normalize(aRes.data)));
      setCategories(normalize(cRes.data));
      setTags(normalize(tRes.data));
    } catch (e) {
      if (isCanceledError(e)) return;
      console.error("[Home] fetch error:", e);
      setErr(e);
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    const controller = new AbortController();
    fetchAll(controller.signal);
    return () => controller.abort();
  }, [fetchAll, lang]);

  useEffect(() => {
    let mounted = true;
    const token = (() => {
      try {
        return localStorage.getItem("accessToken") || localStorage.getItem("token") || null;
      } catch {
        return null;
      }
    })();
    if (!token) return;

    const controller = new AbortController();
    const isJwtLike = token.split(".").length >= 3;
    const headers = {
      Authorization: isJwtLike ? `Bearer ${token}` : `Token ${token}`,
      "Accept-Language": localStorage.getItem(STORAGE_LANG_KEY) || lang,
    };

    setCurrentUserLoading(true);
    api.get("/auth/users/me/", { headers, signal: controller.signal })
      .then((res) => {
        if (mounted) setCurrentUser(res.data || null);
      })
      .catch((err) => {
        if (isCanceledError(err)) return;
      })
      .finally(() => {
        if (mounted) setCurrentUserLoading(false);
      });

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [lang]);

  const categoryArticles = useMemo(() => {
    const slugSet = new Set(categories.map((c) => String(c.slug)));
    const idSet = new Set(categories.map((c) => String(c.id)));
    const map = new Map();
    for (const art of articles) {
      for (const key of getArticleCatKeys(art)) {
        let chosen = slugSet.has(key) ? key : idSet.has(key) ? key : null;
        if (!chosen) continue;
        if (!map.has(chosen)) map.set(chosen, []);
        map.get(chosen).push(art);
      }
    }
    for (const [k, list] of map.entries()) map.set(k, sortByDateDesc(list));
    return map;
  }, [articles, categories]);

  const categorySlices = useMemo(() => {
    const bySlug = new Map(categories.map((c) => [String(c.slug), c]));
    const candidateSlugs = [];
    if (bySlug.has("news")) candidateSlugs.push("news");
    if (bySlug.has("featured")) candidateSlugs.push("featured");

    const populated = categories
      .map((c) => {
        const key = categoryArticles.has(String(c.slug))
          ? String(c.slug)
          : categoryArticles.has(String(c.id))
          ? String(c.id)
          : null;
        return {
          slug: String(c.slug),
          id: String(c.id),
          name: c.name,
          count: key ? categoryArticles.get(key)?.length || 0 : 0,
        };
      })
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

    for (const p of populated) {
      if (!candidateSlugs.includes(p.slug) && p.count > 0) candidateSlugs.push(p.slug);
      if (candidateSlugs.length >= 6) break;
    }

    const slices = [];
    for (const slug of candidateSlugs) {
      const cat = bySlug.get(slug);
      const key = categoryArticles.has(slug)
        ? slug
        : categoryArticles.has(String(cat?.id))
        ? String(cat.id)
        : null;
      const items = key ? categoryArticles.get(key) || [] : [];
      if (items.length) slices.push({ title: cat?.name || slug, articles: items });
      if (slices.length === 3) break;
    }
    // NOTE: we intentionally do NOT pad missing slots with a generic
    // "News" chunk of arbitrary articles anymore. A slice should only
    // exist when it maps to a real category name — if the site doesn't
    // have 3 populated categories yet, we simply render fewer category
    // sections (the sections themselves are already guarded by
    // `.length > 0` checks in the render).
    return slices;
  }, [categories, categoryArticles, articles]);

  const [sliceA, sliceB, sliceC] = categorySlices;

  // Build every home-page section from ONE shared "usedKeys" pool so that no
  // article can ever appear twice across Hero / Trending / the 3 grid
  // sections / the "latest" list / the "popular" sidebar. Sections are
  // filled in visual (top-to-bottom) order, each one claiming articles
  // before the next section gets a chance to reuse them.
  const homeSections = useMemo(() => {
    const usedKeys = new Set();

    const keyOf = (a) => String(a?.slug ?? a?.id ?? "");

    const pickUnique = (pool, count) => {
      const out = [];
      for (const a of pool) {
        const k = keyOf(a);
        if (!k || usedKeys.has(k)) continue;
        out.push(a);
        usedKeys.add(k);
        if (out.length >= count) break;
      }
      return out;
    };

    // Try the section's own pool first, then top up from the full article
    // list (still respecting usedKeys) so sections never look empty.
    const pickUniqueWithFallback = (primaryPool, count) => {
      const out = pickUnique(primaryPool, count);
      if (out.length < count) out.push(...pickUnique(articles, count - out.length));
      return out;
    };

    const heroItems = pickUniqueWithFallback(
      uniqBySlugId([...(sliceA?.articles || []), ...articles]),
      4
    );
    const trending = pickUniqueWithFallback(
      sliceA?.articles?.length ? sliceA.articles : articles,
      4
    );
    // Only build these two category-grid sections when we actually have a
    // real category behind them — otherwise leave them empty so the
    // section is hidden entirely instead of showing generic articles
    // under a blank/undefined title.
    const sliceAItems = sliceA ? pickUniqueWithFallback(sliceA.articles || [], 3) : [];
    const sliceBItems = sliceB ? pickUniqueWithFallback(sliceB.articles || [], 3) : [];
    // "Latest Stories" is intentionally category-agnostic: it always pulls
    // the truly most recent articles across the whole site (the `articles`
    // array is already sorted newest-first), never filtered to one
    // category. usedKeys still guarantees no overlap with sections above.
    const listPosts = pickUniqueWithFallback(articles, 12);

    // "Popular" is a ranking widget (most-read), not a feed section — real
    // news sites always let it repeat articles shown elsewhere on the page.
    // It intentionally does NOT go through usedKeys, so it still shows the
    // top articles even on sites with few total published articles (where
    // the earlier sections above may have already claimed everything).
    const popular = articles.slice(0, 6);

    return { heroItems, trending, sliceAItems, sliceBItems, listPosts, popular };
  }, [sliceA, sliceB, sliceC, articles]);

  const { heroItems, trending, sliceAItems, sliceBItems, listPosts, popular } = homeSections;
  const empty = !loading && !err && articles.length === 0;

  const getAvatarUrl = (user) =>
    user?.avatar || user?.profile_image || user?.image || user?.avatar_url || null;

  const avatar = getAvatarUrl(currentUser);
  const initials = currentUser
    ? currentUser.first_name || currentUser.last_name
      ? `${(currentUser.first_name || " ").charAt(0)}${(currentUser.last_name || " ").charAt(0)}`.toUpperCase()
      : (currentUser.username || "U").slice(0, 2).toUpperCase()
    : "U";

  const pageTitle = "24News";
  const pageDescription =
    lang === "ar"
      ? "آخر الأخبار والمقالات والموضوعات الرائجة على 24News."
      : "Latest news, featured stories, trending topics, and articles on 24News.";

  return (
    <>
      <Helmet
  htmlAttributes={{
    lang: lang || "en",
    dir: lang === "ar" ? "rtl" : "ltr",
  }}
>
  <title>{pageTitle}</title>
  <meta name="description" content={pageDescription} />

  {/* Canonical */}
  <link rel="canonical" href={`${window.location.origin}/${lang}/`} />

  {/* hreflang */}
  <link rel="alternate" hrefLang="en" href={`${window.location.origin}/en/`} />
  <link rel="alternate" hrefLang="fr" href={`${window.location.origin}/fr/`} />
  <link rel="alternate" hrefLang="ar" href={`${window.location.origin}/ar/`} />
  <link rel="alternate" hrefLang="de" href={`${window.location.origin}/de/`} />
  <link rel="alternate" hrefLang="x-default" href={`${window.location.origin}/en/`} />


  {/* Open Graph */}
  <meta property="og:type" content="website" />
  <meta property="og:title" content={pageTitle} />
  <meta property="og:description" content={pageDescription} />
  <meta property="og:url" content={`${window.location.origin}/${lang}/`} />
  <meta property="og:site_name" content="24News" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={pageTitle} />
  <meta name="twitter:description" content={pageDescription} />
  <meta name="twitter:url" content={`${window.location.origin}/${lang}/`} />
  <meta name="twitter:site" content="@24News" />

  {/* Structured Data */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: pageTitle,
      description: pageDescription,
      url: `${window.location.origin}/${lang}/`,
    })}
  </script>

</Helmet>

      {/* Profile button */}
      <Link
        to="/profile"
        className="profile-btn"
        title={currentUser?.username || "Profile"}
        aria-label="Profile"
        style={{ textDecoration: "none" }}
      >
        {avatar ? (
          <img
            src={avatar}
            alt={currentUser?.username || "Profile"}
            style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/default-avatar.png";
            }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 600,
            }}
          >
            {initials}
          </div>
        )}
      </Link>

      {/* ── Hero ── */}
      {loading ? (
        <HeroSkeleton />
      ) : heroItems.length ? (
        <FeaturedGrid articles={heroItems} fullBleed />
      ) : (
        <div className="container py-5">
          <div className="alert alert-info">
            {t("home_no_stories") || "No stories to show right now. Please check back soon."}
          </div>
        </div>
      )}

      {/*
        This is a public page — a logged-out visitor should never see a raw
        "Load error" banner, and a 401/403 here almost always just means a
        stale/expired auth token got attached to the request (see lib/api.js
        and Navbar.jsx), not a real problem with the page's own public data.
        We still log it to the console for debugging, but we only ever show
        a visible banner for genuine, non-auth failures (network/server
        errors), and even then with friendly wording — never err.message
        (which can leak technical/internal details to end users).
      */}
      {err && !isCanceledError(err) && err?.response?.status !== 401 && err?.response?.status !== 403 && (
        <div className="container">
          <div className="alert alert-danger my-4">
            {t("home_load_error") || "Something went wrong loading this page. Please try refreshing."}
          </div>
        </div>
      )}

      {!err && (
        <>
          {trending.length > 0 && <TrendingSection articles={trending} />}

          {sliceAItems.length > 0 && (
            <NewsGridThree title={sliceA?.title} articles={sliceAItems} />
          )}

          {sliceBItems.length > 0 && (
            <NewsGridThreePlay title={sliceB?.title} articles={sliceBItems} />
          )}

          {listPosts.length > 0 && (
            <NewsWithSidebar
              posts={listPosts}
              tags={tags}
              popular={popular}
            />
          )}

          {empty && (
            <div className="container">
              <div className="alert alert-warning my-4">
                No articles to display. Ensure your API returns published items.
              </div>
            </div>
          )}
        </>
      )}

      <style>{`
        .profile-btn {
          position: fixed;
          top: 16px;
          right: 16px;
          z-index: 1200;
          width: 44px;
          height: 44px;
          border-radius: 999px;
          background: #ffffff;
          box-shadow: 0 6px 18px rgba(0,0,0,0.12);
          display: inline-block;
          overflow: hidden;
          border: 1px solid rgba(0,0,0,0.06);
        }

        @media (max-width: 576px) {
          .profile-btn { top: 10px; right: 10px; width: 40px; height: 40px; }
        }

        .hp-search-wrap {
          max-width: 720px;
          margin: 0 auto;
          padding: 20px 16px 4px;
          position: relative;
          z-index: 200;
        }

        @media (max-width: 600px) {
          .hp-search-wrap { padding: 12px 12px 4px; }
        }

        .hp-ad {
          width: 100%;
          overflow: hidden;
        }

        .hp-ad--leaderboard {
          max-width: 1320px;
          margin: 0 auto;
          padding: 12px clamp(14px, 4vw, 32px);
        }

        .hp-ad--rect {
          max-width: 360px;
          margin: 0 auto;
          padding: 16px clamp(14px, 4vw, 32px);
        }

        @media (max-width: 600px) {
          .hp-ad--leaderboard { padding: 10px 12px; }
          .hp-ad--rect { max-width: 100%; padding: 10px 12px; }
        }
      `}</style>
    </>
  );
};

export default HomePage;
